import React, {
  PureComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import withIsMobile, {
  WithIsMobileInjectedProps,
} from '../../../../hoc/withIsMobile';
import SurveyItem from '../../util/SurveyItem';
import clsx from 'clsx';
import Root from './Root';
import SurveyPageHeader from './SurveyPageHeader';
import { Divider } from '@mui/material';
import { getTestProps } from '../../../../util/testProps';

export const testIds = {
  pinnedChrome: 'survey-pinned-chrome',
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      minHeight: '100%',
    },
    paper: {
      background: theme.palette.common.white,
      overflowX: 'auto',
    },
    // Sibling of the scrolling items, not nested in a formLine. CSS sticky
    // inside a short flex line never activates; this wrapper is a child of
    // the paper whose height is the full page, so the strip can pin.
    pinnedChrome: {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      // Must be opaque so scrolling items do not show through, and must match
      // the surface behind it or the strip reads as a gray band. background
      // .paper is grey[50], not white, so it does not match the white Paper.
      backgroundColor: theme.palette.common.white,
    },
    // On mobile Root makes the Paper transparent, so the strip's surface is
    // the page background rather than the white paper.
    pinnedChromeMobile: {
      backgroundColor: theme.palette.background.default,
    },
    pinnedChromeStuck: {
      paddingBottom: theme.spacing(1),
      boxShadow: `0 1px 0 ${theme.palette.divider}`,
    },
    pinnedChromeSentinel: {
      height: 1,
      marginBottom: -1,
      pointerEvents: 'none',
    },
    itemsContainer: {
      width: '100%',
      overflowX: 'auto',
      flex: 1,
    },
    controlContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row-reverse',
      alignItems: 'center',
      marginTop: theme.spacing(3),
      padding: theme.spacing(4),
      paddingTop: theme.spacing(2),
    },
    mobileControlContainer: {
      padding: 0,
    },
    divider: {
      marginTop: theme.spacing(3),
    },
  });

export interface SurveyLayoutOwnProps {
  isPaginated: boolean;
  surveyContent: any;
  leftButton?: any;
  draftButton: any;
  rightButton: any;
  surveyItem?: SurveyItem;
  /** Confirm-strip groups, rendered outside the scrolling item list. */
  pinnedContent?: ReactNode;
}

export type SurveyLayoutProps = SurveyLayoutOwnProps &
  WithStyles<typeof styles> &
  WithIsMobileInjectedProps;

const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const StickyPinnedChrome: React.FC<{
  children: ReactNode;
  className: string;
  stuckClassName: string;
  sentinelClassName: string;
}> = ({ children, className, stuckClassName, sentinelClassName }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { root: findScrollParent(sentinel), threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} className={sentinelClassName} aria-hidden />
      <div
        className={clsx(className, stuck && stuckClassName)}
        {...getTestProps(testIds.pinnedChrome)}
      >
        {children}
      </div>
    </>
  );
};

class SurveyLayout extends PureComponent<SurveyLayoutProps> {
  render() {
    const {
      surveyContent,
      leftButton,
      draftButton,
      rightButton,
      surveyItem,
      pinnedContent,
      isMobile,
      classes,
    } = this.props;

    return (
      <>
        <Root isMobile={isMobile}>
          {surveyItem && <SurveyPageHeader surveyItem={surveyItem} />}
          {pinnedContent ? (
            <StickyPinnedChrome
              className={clsx(
                classes.pinnedChrome,
                isMobile && classes.pinnedChromeMobile,
              )}
              stuckClassName={classes.pinnedChromeStuck}
              sentinelClassName={classes.pinnedChromeSentinel}
            >
              {pinnedContent}
            </StickyPinnedChrome>
          ) : null}
          <div className={classes.itemsContainer}>{surveyContent}</div>
          {isMobile && (rightButton || leftButton) && (
            <>
              <Divider className={classes.divider} />
              <div
                className={clsx(
                  classes.controlContainer,
                  classes.mobileControlContainer,
                )}
              >
                {rightButton}
                {draftButton}
                {leftButton}
              </div>
            </>
          )}
        </Root>
        {!isMobile && (
          <div className={classes.controlContainer}>
            {rightButton}
            {draftButton}
            {leftButton}
          </div>
        )}
      </>
    );
  }
}

export default withStyles(styles)(withIsMobile(SurveyLayout));
